import MicroModal from 'micromodal';
import naja from 'naja';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

export default class Modal {
    private static currentVideoSrc: string | null = null;

    static init(): void {
        // Setup video click handler FIRST, before MicroModal init
        Modal.setupVideoClickHandler();

        MicroModal.init({
            openTrigger: 'data-micromodal-trigger',
            closeTrigger: 'data-micromodal-close',
            disableScroll: false,
            disableFocus: false,
            awaitOpenAnimation: false,
            awaitCloseAnimation: false,
            onShow: (modal) => {
                const modalElement = modal as unknown as HTMLElement;

                const modalContent = modalElement?.querySelector('.modal__container') as HTMLElement;
                if (modalContent) {
                    disableBodyScroll(modalContent);
                }

                // Handle video modal
                Modal.handleVideoModal(modalElement);
            },
            onClose: (modal) => {
                const modalElement = modal as unknown as HTMLElement;

                // Clear all body scroll locks to ensure body is unlocked
                clearAllBodyScrollLocks();

                // Force remove any inline styles that might have been added
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.top = '';

                // Remove any classes that might lock the body
                document.body.classList.remove('modal-open', 'is-locked');

                // Clear video iframe
                Modal.clearVideoModal(modalElement);
            },
        });

        Modal.setupCustomOpenModal();
        Modal.setupSnippetListener();
    }

    private static setupVideoClickHandler(): void {
        // Use capture phase to run before MicroModal's handler
        document.addEventListener(
            'click',
            (e) => {
                const target = e.target as HTMLElement;
                const videoItem = target.closest(
                    '.js-video-item[data-micromodal-trigger="video-modal"]',
                ) as HTMLElement;

                if (videoItem) {
                    const videoSrc = videoItem.getAttribute('data-video-src');
                    if (videoSrc) {
                        Modal.currentVideoSrc = videoSrc;
                        //console.log('Stored video src in capture phase:', Modal.currentVideoSrc);
                    }
                }
            },
            true,
        ); // true = use capture phase
    }

    private static handleVideoModal(modal: HTMLElement): void {
        if (!modal || modal.id !== 'video-modal') return;

        const iframe = modal.querySelector('.js-video-modal-item iframe') as HTMLIFrameElement;
        if (!iframe) return;

        //console.log('Setting iframe src to:', Modal.currentVideoSrc);
        if (Modal.currentVideoSrc) {
            iframe.src = Modal.currentVideoSrc;
        }
    }

    private static clearVideoModal(modal: HTMLElement): void {
        if (!modal || modal.id !== 'video-modal') return;

        const iframe = modal.querySelector('.js-video-modal-item iframe') as HTMLIFrameElement;
        if (iframe) {
            iframe.src = '';
        }

        // Clear the stored video src
        Modal.currentVideoSrc = null;
    }

    private static setupCustomOpenModal(): void {
        // Add explicit handler for close buttons to ensure body scroll is restored
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target && target.closest('[data-micromodal-close]')) {
                // Force clear all body locks when any close button is clicked
                setTimeout(() => {
                    clearAllBodyScrollLocks();
                    document.body.style.overflow = '';
                    document.body.style.position = '';
                    document.body.style.width = '';
                    document.body.style.top = '';
                    document.body.classList.remove('modal-open', 'is-locked');
                }, 100);
            }
        });
    }

    private static setupSnippetListener(): void {
        naja.snippetHandler.addEventListener('afterUpdate', () => {
            // Video click handler is on document level, so no need to reinit
        });
    }
}
